import type { ObjectTypeKey } from "./objectType";

import * as z from "zod";

import { integrationScopeSchema } from "./agentIntegration";
import { knownIntegrationKeys } from "./agentTools/remote/integrations";
import { modifiableElementTypeSchema } from "./fileModifiers";
import { productSchema } from "./product";
import { registryItemTypeSchema } from "./registryCatalog";
import {
  assetSearchOutputSchema,
  generatePageOutputSchema,
  globOutputSchema,
  grepOutputSchema,
  inspirationOutputSchema,
  integrationConfigOutputSchema,
  lsOutputSchema,
  productSearchOutputSchema,
  readOutputSchema,
  staticAnalysisOutputSchema,
  suggestTemplatesOutputSchema,
  urlOutputSchema,
} from "./toolOutput";

const chatMessageRoleSchema = z.enum(["user", "assistant", "system", "tool"]);

export const toolNameSchema = z
  .enum([
    "getIntegrationConfigTool",
    "inputProcessorTool",
    "suggestTemplatesTool",
    "generatePageTool",
    "assetSearchTool",
    "getInspirationTool",
    "URLTool",
    "editTool",
    "staticAnalysisTool",
    "readTool",
    "lsTool",
    "planWriteTool",
    "todoWriteTool",
    "productSearchTool",
    "replicationTool",
    "globTool",
    "grepTool",
    "activatePageGenModeTool",
  ])
  .describe("ToolName");

const chatMessageContentBaseSchema = z.object({
  fileId: z.string().optional(),
  llmRequestId: z.string().optional(),
  braintrustSpanId: z.string().optional(),
});

const toolResultBaseSchema = chatMessageContentBaseSchema.extend({
  type: z.literal("tool-result"),
  toolCallId: z.string(),
});

const toolResultOutputValueJsonBaseSchema = z.object({
  success: z.boolean(),
  isCanceled: z.boolean().optional(),
  message: z.string().optional(),
});

export const toolResultOutputValueContentItemSchema = z
  .discriminatedUnion("type", [
    z.object({
      type: z.literal("text"),
      text: z.string(),
    }),
    z.object({
      type: z.literal("media"),
      data: z.string(),
      mediaType: z.string(),
    }),
  ])
  .describe("ToolResultOutputValueContentItem");

const getIntegrationConfigToolResultSchema = toolResultBaseSchema.extend({
  toolName: z.literal("getIntegrationConfigTool"),
  output: z.object({
    type: z.literal("json"),
    value: toolResultOutputValueJsonBaseSchema.extend({
      ...integrationConfigOutputSchema.shape,
    }),
  }),
});

const inputProcessorToolResultSchema = toolResultBaseSchema.extend({
  toolName: z.literal("inputProcessorTool"),
  output: z.object({
    type: z.literal("json"),
    value: toolResultOutputValueJsonBaseSchema,
  }),
});

export const suggestTemplatesToolResultSchema = toolResultBaseSchema
  .extend({
    toolName: z.literal("suggestTemplatesTool"),
    output: z.object({
      type: z.literal("json"),
      value: toolResultOutputValueJsonBaseSchema.extend({
        ...suggestTemplatesOutputSchema.shape,
      }),
    }),
  })
  .describe("SuggestTemplatesToolResult");

export const generatePageToolResultSchema = toolResultBaseSchema
  .extend({
    toolName: z.literal("generatePageTool"),
    output: z.object({
      type: z.literal("json"),
      value: toolResultOutputValueJsonBaseSchema.extend({
        ...generatePageOutputSchema.shape,
      }),
    }),
  })
  .describe("GeneratePageToolResult");

export const assetSearchToolResultSchema = toolResultBaseSchema
  .extend({
    toolName: z.literal("assetSearchTool"),
    output: z.discriminatedUnion("type", [
      z.object({
        type: z.literal("content"),
        value: z.array(toolResultOutputValueContentItemSchema),
      }),
      z.object({
        type: z.literal("json"),
        value: toolResultOutputValueJsonBaseSchema.extend({
          ...assetSearchOutputSchema.shape,
        }),
      }),
    ]),
  })
  .describe("AssetSearchToolResult");

export const getInspirationToolResultSchema = toolResultBaseSchema
  .extend({
    toolName: z.literal("getInspirationTool"),
    output: z.discriminatedUnion("type", [
      z.object({
        type: z.literal("content"),
        value: z.array(toolResultOutputValueContentItemSchema),
      }),
      z.object({
        type: z.literal("json"),
        value: toolResultOutputValueJsonBaseSchema.extend({
          ...inspirationOutputSchema.shape,
        }),
      }),
    ]),
  })
  .describe("GetInspirationToolResult");

export const URLToolResultSchema = toolResultBaseSchema
  .extend({
    toolName: z.literal("URLTool"),
    output: z.discriminatedUnion("type", [
      z.object({
        type: z.literal("content"),
        value: z.array(toolResultOutputValueContentItemSchema),
      }),
      z.object({
        type: z.literal("json"),
        value: toolResultOutputValueJsonBaseSchema.extend({
          ...urlOutputSchema.shape,
        }),
      }),
    ]),
  })
  .describe("URLToolResult");

export const editToolResultSchema = toolResultBaseSchema
  .extend({
    toolName: z.literal("editTool"),
    output: z.object({
      type: z.literal("json"),
      value: toolResultOutputValueJsonBaseSchema,
    }),
  })
  .describe("EditToolResult");

export const staticAnalysisToolResultSchema = toolResultBaseSchema
  .extend({
    toolName: z.literal("staticAnalysisTool"),
    output: z.object({
      type: z.literal("json"),
      value: toolResultOutputValueJsonBaseSchema.extend({
        ...staticAnalysisOutputSchema.shape,
      }),
    }),
  })
  .describe("StaticAnalysisToolResult");

export const readToolResultSchema = toolResultBaseSchema
  .extend({
    toolName: z.literal("readTool"),
    output: z.object({
      type: z.literal("json"),
      value: toolResultOutputValueJsonBaseSchema.extend({
        ...readOutputSchema.shape,
      }),
    }),
  })
  .describe("ReadToolResult");

export const lsToolResultSchema = toolResultBaseSchema.extend({
  toolName: z.literal("lsTool"),
  output: z.object({
    type: z.literal("json"),
    value: toolResultOutputValueJsonBaseSchema.extend({
      ...lsOutputSchema.shape,
    }),
  }),
});

export const planWriteToolResultSchema = toolResultBaseSchema
  .extend({
    toolName: z.literal("planWriteTool"),
    output: z.object({
      type: z.literal("json"),
      value: toolResultOutputValueJsonBaseSchema.extend({
        plan: z.string().optional(),
      }),
    }),
  })
  .describe("PlanWriteToolResult");

export const productSearchToolResultSchema = toolResultBaseSchema
  .extend({
    toolName: z.literal("productSearchTool"),
    output: z.object({
      type: z.literal("json"),
      value: toolResultOutputValueJsonBaseSchema.extend({
        ...productSearchOutputSchema.shape,
      }),
    }),
  })
  .describe("ProductSearchToolResult");

export const replicationToolResultSchema = toolResultBaseSchema
  .extend({
    toolName: z.literal("replicationTool"),
    output: z.object({
      type: z.literal("json"),
      value: toolResultOutputValueJsonBaseSchema.extend({
        lineCount: z.number().optional(),
        pageFileContent: z.string().optional(),
        runId: z.string().optional(),
      }),
    }),
  })
  .describe("ReplicationToolResult");

export const globToolResultSchema = toolResultBaseSchema
  .extend({
    toolName: z.literal("globTool"),
    output: z.object({
      type: z.literal("json"),
      value: toolResultOutputValueJsonBaseSchema.extend({
        ...globOutputSchema.shape,
      }),
    }),
  })
  .describe("GlobToolResult");

export const grepToolResultSchema = toolResultBaseSchema
  .extend({
    toolName: z.literal("grepTool"),
    output: z.object({
      type: z.literal("json"),
      value: toolResultOutputValueJsonBaseSchema.extend({
        ...grepOutputSchema.shape,
      }),
    }),
  })
  .describe("GrepToolResult");

export const todoItemStatusSchema = z
  .enum(["pending", "in_progress", "completed"])
  .describe("TodoItemStatus");

export const todoItemSchema = z
  .object({
    content: z.string(),
    status: todoItemStatusSchema,
    activeForm: z.string(), // the active form of the todo item, e.g. "Fix authentication bug" -> "Fixing authentication bug"
  })
  .describe("TodoItem");

export const todoWriteToolResultSchema = toolResultBaseSchema
  .extend({
    toolName: z.literal("todoWriteTool"),
    output: z.object({
      type: z.literal("json"),
      value: toolResultOutputValueJsonBaseSchema.extend({
        todos: z.array(todoItemSchema).optional(),
      }),
    }),
  })
  .describe("TodoWriteToolResult");

export const activatePageGenModeToolResultSchema = toolResultBaseSchema
  .extend({
    toolName: z.literal("activatePageGenModeTool"),
    output: z.object({
      type: z.literal("json"),
      value: toolResultOutputValueJsonBaseSchema,
    }),
  })
  .describe("ActivatePageGenModeToolResult");

const toolResultSchema = z.discriminatedUnion("toolName", [
  getIntegrationConfigToolResultSchema,
  inputProcessorToolResultSchema,
  suggestTemplatesToolResultSchema,
  generatePageToolResultSchema,
  assetSearchToolResultSchema,
  getInspirationToolResultSchema,
  URLToolResultSchema,
  editToolResultSchema,
  staticAnalysisToolResultSchema,
  readToolResultSchema,
  lsToolResultSchema,
  planWriteToolResultSchema,
  todoWriteToolResultSchema,
  productSearchToolResultSchema,
  replicationToolResultSchema,
  globToolResultSchema,
  grepToolResultSchema,
  activatePageGenModeToolResultSchema,
]);

export const chatMessageContentSchema = z
  .array(
    z.discriminatedUnion("type", [
      chatMessageContentBaseSchema.extend({
        type: z.literal("text"),
        text: z.string(),
        // NOTE (Bobby: 2025-06-25): It is default text if not provided. This specifies the content inside the text field
        // and alternative content types like products can be parsed into a object like ChatProductItem
        contentType: z
          .enum([
            "text",
            "product",
            "buildError",
            "revert",
            "selectedTemplate",
            "generatedPage",
            "codeChangesOverview",
            "toolSectionHeader",
            "generalError",
            "canceled",
            "integrationStatus",
          ])
          .optional(),
        isHidden: z.boolean().optional(),
        isNewMessageGroup: z.boolean().optional(),
      }),
      chatMessageContentBaseSchema.extend({
        type: z.literal("tool-call"),
        toolCallId: z.string(),
        toolName: toolNameSchema,
        input: z.any().optional(),
      }),
      toolResultSchema,
      chatMessageContentBaseSchema.extend({
        type: z.literal("image"),
        id: z.string().optional(),
        image: z.string(),
        isHidden: z.boolean().optional(),
        isValidChatAsset: z.boolean().optional(),
      }),
      // NOTE (Kevin, 2025-12-12): Video content type is used for UI display only.
      // Videos are not sent to the LLM (which doesn't support video input).
      chatMessageContentBaseSchema.extend({
        type: z.literal("video"),
        id: z.string().optional(),
        video: z.string(),
        isHidden: z.boolean().optional(),
      }),
    ]),
  )
  .describe("ChatMessageContent");

export const chatMessageSchema = z
  .object({
    id: z.uuid(),
    createdAt: z.coerce.date(),
    role: chatMessageRoleSchema,
    content: chatMessageContentSchema,
    feedback: z.enum(["positive", "negative", "none"]),
    chatId: z.uuid(),
    chatTurnId: z.uuid(),
  })
  .describe("ChatMessage");

export const codeChangeToolCallProgressStepSchema = z
  .discriminatedUnion("stepType", [
    z.object({
      id: z.string(),
      stepType: z.literal("applyingChanges"),
      changes: z.object({
        lineCount: z.number(),
      }),
    }),
    z.object({
      id: z.string(),
      stepType: z.literal("applyingSwarmChanges"),
      changes: z.object({
        lineCount: z.number(),
        totalAgents: z.number(),
        agentsCompleted: z.number(),
      }),
    }),
    z.object({
      id: z.string(),
      stepType: z.literal("determiningSwarmSections"),
    }),
    z.object({
      id: z.string(),
      stepType: z.literal("runningFixerAgent"),
      changes: z
        .object({
          lineCount: z.number(),
        })
        .optional(),
    }),
  ])
  .describe("CodeChangeToolCallProgressStep");

export const toolCallProgressSchema = z
  .discriminatedUnion("toolName", [
    z.object({
      toolName: z.literal("codeChangeTool"),
      steps: z.array(codeChangeToolCallProgressStepSchema),
    }),
    z.object({
      toolName: z.literal("editTool"),
      changes: z.object({
        lineCount: z.number(),
      }),
    }),
  ])
  .describe("ToolCallProgress");

export const chatEventSchema = z
  .discriminatedUnion("type", [
    z.object({
      type: z.literal("message"),
      data: chatMessageSchema,
    }),
    z.object({
      type: z.literal("tool-progress"),
      data: z.object({
        messageId: z.string(),
        toolCallId: z.string(),
        progress: toolCallProgressSchema,
      }),
    }),
  ])
  .describe("ChatEvent");

export const contextObjectSchema = z
  .object({
    openPageId: z.string().nullish(),
  })
  .describe("ContextObject");

// NOTE (Gabe, 2026-01-16): Minimal schema for frontend transmission. Backend enriches before LLM.
export const chatProductItemMinimalSchema = productSchema
  .pick({
    id: true,
    title: true,
    featuredMedia: true,
  })
  .describe("ChatProductItemMinimal");

export const chatProductItemSchema = productSchema
  .pick({
    id: true,
    title: true,
    description: true,
    status: true,
    featuredMedia: true,
    media: true,
    options: true,
    variants: true,
    sellingPlanGroups: true,
  })
  .describe("ChatProductItem");

export const chatBuildErrorItemSchema = z
  .object({
    type: z.literal("buildError"),
    isReverting: z.boolean(),
    erroredVersionId: z.uuid().optional(),
    hasRetried: z.boolean().optional(),
    logs: z.string().optional(),
  })
  .describe("ChatBuildErrorItem");

export const chatGeneralErrorItemSchema = z
  .object({
    type: z.enum(["networkError", "noNetworkError", "unknownError"]),
    errorId: z.string().optional(),
    message: z.string().optional(),
  })
  .describe("ChatGeneralErrorItem");

export const chatCodeChangesOverviewItemSchema = z
  .object({
    versionId: z.string().optional(),
    errorMessage: z.string().optional(),
  })
  .describe("ChatCodeChangesOverviewItem");

export const chatToolSectionHeaderItemSchema = z
  .object({
    streamingText: z.string(),
    completedText: z.string(),
  })
  .describe("ChatToolSectionHeaderItem");

export const chatCanceledItemSchema = z
  .object({
    isCanceled: z.boolean(),
  })
  .describe("ChatCanceledItem");

export const chatAssetItemSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    url: z.string(),
    fileExtension: z.string(),
  })
  .describe("ChatAssetItem");

export const chatIntegrationItemSchema = z
  .object({
    type: z.string(), // IntegrationType as string
    title: z.string(),
    image: z.string(),
  })
  .describe("ChatIntegrationItem");

export const objectTypeKeySchema = z.enum([
  "product",
  "collection",
  "discountCode",
  "shopifyFile",
  "contentfulAsset",
  "airAsset",
  "googleDriveFile",
  "variant",
  "asset",
  "persona",
  "integration",
  "page",
  "siteComponent",
  "skill",
  "registryItem",
  "opusClip",
  "opusProject",
  "opusCollection",
  "segmentSource",
  "intelligemsExperience",
  "customerIoSegment",
  "customerIoCampaign",
  "customerIoBroadcast",
  "levantaProduct",
  "okendoReview",
  "reviewsIoReview",
  "looxReview",
  "loyaltyLionReward",
  "loyaltyLionTier",
  "vwoCampaign",
  "aliaCampaign",
  "plan",
]) satisfies z.ZodType<ObjectTypeKey>;

export const integrationKeySchema = z.enum(knownIntegrationKeys);

export const chatIntegrationObjectItemSchema = z
  .object({
    integrationKey: integrationKeySchema,
    objectType: objectTypeKeySchema,
    object: z.object({
      id: z.string(),
      title: z.string().optional(),
      handle: z.string().optional(),
      filepath: z.string().optional(),
      imageUrl: z.string().nullish(),
      description: z.string().optional(),
      body: z.string().optional(),
      mentionedByUser: z.boolean().optional(),
    }),
  })
  .describe("ChatIntegrationObjectItem");

export const chatRegistryItemTokenDataSchema = z
  .object({
    id: z.string(),
    title: z.string().optional(),
    slug: z.string().optional(),
    registryItemType: registryItemTypeSchema.optional(),
    imageUrl: z.string().nullish(),
    description: z.string().optional(),
  })
  .describe("ChatRegistryItemTokenData");

export const chatPromptSourceSchema = z
  .discriminatedUnion("type", [
    z.object({
      type: z.literal("miniapp"),
      id: z.string().min(1),
    }),
    z.object({
      type: z.literal("select-mode"),
    }),
    z.object({
      type: z.literal("linq"),
    }),
    z.object({
      type: z.literal("slack"),
      workspaceId: z.string().min(1),
      channelId: z.string().min(1),
      messageId: z.string().min(1),
    }),
  ])
  .describe("ChatPromptSource");

export const chatSourcePromptTokenDataSchema = z
  .object({
    source: chatPromptSourceSchema,
    prompt: z.string().min(1).max(50_000),
  })
  .describe("ChatSourcePromptTokenData");

export const chatRevertItemSchema = z
  .object({
    fileVersionId: z.string(),
    context: z.string().optional(),
  })
  .describe("ChatRevertItem");

export const chatSelectedTemplateItemSchema = z
  .object({
    type: z.literal("selectedTemplate"),
    selectedTemplateId: z.string(),
    selectedTemplateName: z.string(),
  })
  .describe("ChatSelectedTemplateItem");

export const chatGeneratedPageItemSchema = z
  .object({
    type: z.literal("generatedPage"),
    pageName: z.string(),
    pageId: z.string(),
  })
  .describe("ChatGeneratedPageItem");

export const chatIntegrationStatusItemSchema = z
  .object({
    integrationKey: z.string(),
    name: z.string(),
    description: z.string(),
    isConnected: z.boolean(),
    connectionId: z.string().optional(),
    scope: integrationScopeSchema,
  })
  .describe("ChatIntegrationStatusItem");

export const chatTokenSchema = z
  .discriminatedUnion("type", [
    z.object({
      type: z.literal("product"),
      data: z.union([chatProductItemMinimalSchema, chatProductItemSchema]),
    }),
    z.object({
      type: z.literal("asset"),
      data: chatAssetItemSchema,
    }),
    z.object({
      type: z.literal("integration"),
      data: chatIntegrationItemSchema,
    }),
    z.object({
      type: z.literal("mentionable-object"),
      data: chatIntegrationObjectItemSchema,
    }),
    z.object({
      type: z.literal("user-mentioned-object"),
      data: chatIntegrationObjectItemSchema,
    }),
    z.object({
      type: z.literal("integration-object"),
      data: chatIntegrationObjectItemSchema,
    }),
    z.object({
      type: z.literal("registry-item"),
      data: chatRegistryItemTokenDataSchema,
    }),
    z.object({
      type: z.literal("source-prompt"),
      data: chatSourcePromptTokenDataSchema,
    }),
    z.object({
      type: z.literal("selected-element"),
      data: z.object({
        elementType: modifiableElementTypeSchema,
        stableElementId: z.string(),
      }),
    }),
  ])
  .describe("ChatToken");

// Read legacy tool payloads permissively; never use these schemas for writes.

export const toolNameReadSchema = z.union([toolNameSchema, z.string()]);

const unknownToolResultSchema = toolResultBaseSchema.extend({
  toolName: z.string(),
  output: z.unknown(),
});

export const toolResultReadSchema = z.union([
  toolResultSchema,
  unknownToolResultSchema,
]);

const chatMessageContentItemReadSchema = z
  .object({
    type: z.string(),
  })
  .passthrough();

export const chatMessageContentReadSchema = z
  .array(chatMessageContentItemReadSchema)
  .describe("ChatMessageContentRead");

export const chatMessageReadSchema = chatMessageSchema
  .omit({
    content: true,
  })
  .extend({
    content: chatMessageContentReadSchema,
  });
